import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from '@mui/material';
import Keyboard from 'react-simple-keyboard';
import 'react-simple-keyboard/build/css/index.css';
import TerminalOrangeButton from '../ui/TerminalOrangeButton';
import { terminalColors } from '../../theme/terminalStyles';
import { useDesktopModalInput } from '../../hooks/useDesktopModalInput';

const dialogPaperSx = {
  bgcolor: terminalColors.gradientTop,
  backgroundImage: 'none',
  borderRadius: 2,
  m: { xs: 1, sm: 2 },
};

const NUMERIC_LAYOUT = {
  default: ['1 2 3', '4 5 6', '7 8 9', '{clearAll} 0 . {bksp}'],
};

const NUMERIC_DISPLAY = {
  '{bksp}': '⌫',
  '{clearAll}': 'C',
};

const StocktakeQuantityEditDialog = ({
  open,
  initialValue,
  onClose,
  onConfirm,
  isSaving,
}) => {
  const [value, setValue] = useState('');
  const keyboardRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const initial = initialValue != null ? String(initialValue) : '0';
    setValue(initial);
    keyboardRef.current?.setInput(initial);
  }, [open, initialValue]);

  const handleClose = () => {
    if (isSaving) return;
    onClose();
  };

  const handleConfirm = useCallback(() => {
    if (isSaving) return;
    onConfirm(value);
  }, [isSaving, onConfirm, value]);

  const { inputRef, handleInputKeyDown } = useDesktopModalInput({
    open,
    onConfirm: handleConfirm,
    enabled: !isSaving,
    focusKey: initialValue,
  });

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ sx: dialogPaperSx }}
    >
      <DialogTitle sx={{ fontWeight: 600, color: terminalColors.textPrimary }}>
        Кількість (факт)
      </DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          variant="outlined"
          value={value}
          inputRef={inputRef}
          onKeyDown={handleInputKeyDown}
          onChange={(e) => {
            const next = e.target.value.replace(',', '.');
            if (/^\d*\.?\d*$/.test(next) || next === '') {
              setValue(next);
              keyboardRef.current?.setInput(next);
            }
          }}
          margin="normal"
          inputProps={{ inputMode: 'decimal' }}
          sx={{ '& .MuiOutlinedInput-root': { bgcolor: terminalColors.cardWhite } }}
        />
        <Keyboard
          keyboardRef={(r) => {
            keyboardRef.current = r;
          }}
          onChange={setValue}
          layout={NUMERIC_LAYOUT}
          display={NUMERIC_DISPLAY}
          theme="hg-theme-default hg-layout-numeric"
          onKeyPress={(button) => {
            if (button === '{clearAll}') {
              setValue('');
              keyboardRef.current?.clearInput();
            }
          }}
        />
      </DialogContent>
      <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
        <TerminalOrangeButton
          fullWidth
          onClick={handleClose}
          disabled={isSaving}
          sx={{ bgcolor: 'transparent', color: 'text.primary', boxShadow: 'none' }}
        >
          Відміна
        </TerminalOrangeButton>
        <TerminalOrangeButton fullWidth onClick={handleConfirm} disabled={isSaving}>
          {isSaving ? <CircularProgress size={22} color="inherit" /> : 'OK'}
        </TerminalOrangeButton>
      </DialogActions>
    </Dialog>
  );
};

export default StocktakeQuantityEditDialog;
